import { ArrowRight, History } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { CircleLoader } from 'common-src/components/base';
import { ColorsNew } from 'common-src/styles';

import { getCareOrchestrationState, saveCareOrchestrationState } from 'src/features/tabsState';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';

import { ACTION_AREA_PAGE } from '../../constants';
import {
  filterApptsByDateForScope,
  formatWeekday,
  getAppointmentEasternDayOffset,
  getAppointmentReadinessStatus,
  getDayHeadingLabel,
  getEasternTodayDateKey,
  getPeriodChipDays,
  getPeriodLabel,
  getScopeReadoutLabel,
  getThisWeekPeriod,
  getTodayPeriod,
  isSamePeriod,
  isTaskReadinessComplete,
  togglePeriodDay,
} from '../../helpers';
import { departmentsInScope, hasActiveScopeFilters } from '../../scope';
import { fetchHuddleNotesBulk, postHuddleNote } from '../../services/careOrchestrationApi';
import DayChip from '../DayChip/DayChip';
import HuddleTaskSheet from '../HuddleTaskSheet';
import HuddleVisitCard from '../HuddleVisitCard';
import PostVisitView from '../PostVisitView';
import ScopeFilterGroup from '../ScopeFilterGroup';
import VisitsDueView from '../VisitsDueView';
import styles from './ActionAreaView.module.scss';

const POST_VISIT_WINDOW_DAYS = 7;

const DayHeading = ({ heading }) => {
  const [day, ...rest] = heading.split(' · ');
  return (
    <h4 className={styles.dayHeading}>
      <span>{day}</span>
      {rest.length > 0 && <span className={styles.dayHeadingQuiet}>· {rest.join(' · ')}</span>}
    </h4>
  );
};

DayHeading.propTypes = {
  heading: PropTypes.string.isRequired,
};

const ActionAreaHeader = ({ active, onChange }) => (
  <div className={styles.pageHeader}>
    <h1 className={styles.pageTitle}>Action Area</h1>
    <div className={styles.segmentedControl} role="group" aria-label="Action Area segment">
      <button
        type="button"
        aria-pressed={active === ACTION_AREA_PAGE.Huddle}
        className={
          active === ACTION_AREA_PAGE.Huddle ? styles.segmentButtonActive : styles.segmentButton
        }
        onClick={
          active === ACTION_AREA_PAGE.Huddle ? undefined : () => onChange(ACTION_AREA_PAGE.Huddle)
        }
      >
        Huddle
      </button>
      <button
        type="button"
        aria-pressed={active === ACTION_AREA_PAGE.VisitsDue}
        className={
          active === ACTION_AREA_PAGE.VisitsDue ? styles.segmentButtonActive : styles.segmentButton
        }
        onClick={
          active === ACTION_AREA_PAGE.VisitsDue
            ? undefined
            : () => onChange(ACTION_AREA_PAGE.VisitsDue)
        }
      >
        Visits due
      </button>
    </div>
  </div>
);

ActionAreaHeader.propTypes = {
  active: PropTypes.oneOf(Object.values(ACTION_AREA_PAGE)).isRequired,
  onChange: PropTypes.func.isRequired,
};

const ActionAreaView = ({
  isLoading,
  apptsByDate,
  postVisitIsLoading,
  postApptsByDate,
  postVisitAppointmentTasksById = {},
  appointmentTasksById = {},
  organizationScope = null,
  taskActions,
  departments = [],
  scopeFilters,
  onChangeScopeFilters,
  guideEnabled = false,
  onToggleGuide,
  isScopePaused = false,
  onToggleScopePause,
  isPinnedToCurrentScope = false,
  canResetToPinnedScope = false,
  showScopeUtilities = false,
  onToggleScopePin,
  onResetScopeToPinned,
}) => {
  const { showAlert, AlertType } = useAlert();
  const dispatch = useDispatch();
  const savedState = useCustomSelector((state) => getCareOrchestrationState(state));

  const todayKey = getEasternTodayDateKey();

  const [period, setPeriod] = useState(() => {
    const savedPeriod = savedState?.actionAreaPeriod;
    if (
      Array.isArray(savedPeriod) &&
      savedPeriod.length &&
      savedPeriod[savedPeriod.length - 1] >= todayKey
    ) {
      return savedPeriod;
    }
    return getTodayPeriod(todayKey);
  });
  const [notesByApptId, setNotesByApptId] = useState({});
  const fetchedNoteRangeSignatureRef = useRef('');
  const [page, setPage] = useState(() => {
    if (savedState?.actionAreaPage === ACTION_AREA_PAGE.PostVisit)
      return ACTION_AREA_PAGE.PostVisit;
    if (savedState?.actionAreaPage === ACTION_AREA_PAGE.VisitsDue)
      return ACTION_AREA_PAGE.VisitsDue;
    return ACTION_AREA_PAGE.Huddle;
  });
  const [openTask, setOpenTask] = useState(null);

  useEffect(() => {
    dispatch(saveCareOrchestrationState({ actionAreaPeriod: period }));
  }, [dispatch, period]);

  useEffect(() => {
    dispatch(saveCareOrchestrationState({ actionAreaPage: page }));
  }, [dispatch, page]);

  const handleOpenTask = (task, apptId, relatedTasks) =>
    setOpenTask({ task, apptId, relatedTasks });

  const handleSelectRelatedTask = (task) => setOpenTask((prev) => ({ ...prev, task }));

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== 'Escape' || openTask) return;
      if (page === ACTION_AREA_PAGE.PostVisit) setPage(ACTION_AREA_PAGE.Huddle);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [openTask, page]);

  const handlePostNote = async (appt, text) => {
    try {
      const note = await postHuddleNote(appt.id, text);
      setNotesByApptId((prev) => ({ ...prev, [appt.id]: [...(prev[appt.id] || []), note] }));
      showAlert(AlertType.Success, '', `Note posted for ${appt?.patient?.name || 'this visit'}.`);
    } catch (error) {
      showAlert(AlertType.Error, '', error?.message || 'Failed to post note');
    }
  };

  const scopedDepartmentIds = useMemo(() => {
    const scoped = departmentsInScope(departments, scopeFilters, guideEnabled);
    return new Set(scoped.map((department) => `${department.id}`));
  }, [departments, scopeFilters, guideEnabled]);

  const isAllScope = isScopePaused || (!hasActiveScopeFilters(scopeFilters) && !guideEnabled);

  const { filteredByDate } = useMemo(
    () => filterApptsByDateForScope(apptsByDate, { isAllScope, scopedDepartmentIds }),
    [apptsByDate, isAllScope, scopedDepartmentIds],
  );

  const chipDays = useMemo(() => getPeriodChipDays(todayKey), [todayKey]);
  const thisWeekPeriod = useMemo(() => getThisWeekPeriod(todayKey), [todayKey]);
  const todayPeriod = useMemo(() => getTodayPeriod(todayKey), [todayKey]);

  const countForDay = (dayKey) => (filteredByDate[dayKey] || []).length;
  const periodPatientCount = period.reduce((sum, dayKey) => sum + countForDay(dayKey), 0);

  const periodLabel = getPeriodLabel(todayKey, period);
  const scopeLabel = getScopeReadoutLabel(scopeFilters, isAllScope);
  const pickedDate = period.length === 1 ? period[0] : '';

  const presets = [
    { label: 'Today', value: todayPeriod },
    { label: 'This week', value: thisWeekPeriod },
  ];

  const { filteredByDate: postFilteredByDate } = useMemo(
    () => filterApptsByDateForScope(postApptsByDate, { isAllScope, scopedDepartmentIds }),
    [postApptsByDate, isAllScope, scopedDepartmentIds],
  );

  const postVisitSummary = useMemo(() => {
    let open = 0;
    let visits = 0;
    Object.values(postFilteredByDate)
      .flat()
      .forEach((appointment) => {
        const dayOffset = getAppointmentEasternDayOffset(appointment);
        if (dayOffset === null || dayOffset > 0 || dayOffset < -POST_VISIT_WINDOW_DAYS) return;
        const tasks = postVisitAppointmentTasksById[appointment.id] || [];
        const openCount = tasks.filter((task) => !isTaskReadinessComplete(task)).length;
        if (openCount > 0) {
          open += openCount;
          visits += 1;
        }
      });
    return { open, visits };
  }, [postFilteredByDate, postVisitAppointmentTasksById]);

  const getDaySummary = (appts) => {
    const summary = { blocked: 0, atRisk: 0, ready: 0 };
    appts.forEach((appt) => {
      const status = getAppointmentReadinessStatus(appointmentTasksById[appt.id] || []);
      if (status === 'red') summary.blocked += 1;
      else if (status === 'yellow') summary.atRisk += 1;
      else if (status === 'green') summary.ready += 1;
    });
    return summary;
  };

  const periodDayGroups = useMemo(
    () =>
      period.map((dayKey) => ({
        dayKey,
        appts: (filteredByDate[dayKey] || [])
          .slice()
          .sort((a, b) => (a.time || '').localeCompare(b.time || '')),
      })),
    [period, filteredByDate],
  );

  const periodAppts = periodDayGroups.flatMap((group) => group.appts);
  const listTitle =
    period.length <= 1
      ? 'Patients through the day'
      : isSamePeriod(period, thisWeekPeriod)
        ? 'Patients through the week'
        : `Patients across ${period.length} days`;
  const periodSummary = getDaySummary(periodAppts);
  const periodSummaryLabel = `${periodSummary.blocked} blocked · ${periodSummary.atRisk} at risk · ${periodSummary.ready} ready`;

  useEffect(() => {
    const dateKeys = Object.keys(apptsByDate).filter(Boolean).sort();
    if (!dateKeys.length) return;
    const dateFrom = dateKeys[0];
    const dateTo = dateKeys[dateKeys.length - 1];
    const signature = `${dateFrom}|${dateTo}|${organizationScope || ''}`;
    if (signature === fetchedNoteRangeSignatureRef.current) return;
    fetchedNoteRangeSignatureRef.current = signature;

    fetchHuddleNotesBulk({ dateFrom, dateTo, organizationScope })
      .then((body) => {
        const grouped = {};
        (body?.data || []).forEach((note) => {
          (grouped[note.appointmentId] = grouped[note.appointmentId] || []).push(note);
        });
        setNotesByApptId(grouped);
      })
      .catch(() => {
        fetchedNoteRangeSignatureRef.current = '';
      });
  }, [apptsByDate, organizationScope]);

  const taskSheet = openTask && (
    <HuddleTaskSheet
      onClose={() => setOpenTask(null)}
      taskDetailProps={{
        task: openTask.task,
        relatedTasks: openTask.relatedTasks || [],
        cachedDelegatedSubtasks: openTask.task?.parentTaskId
          ? undefined
          : taskActions.delegatedSubtasksByParentId?.[`${openTask.task.id}`] || [],
        onSelectTask: handleSelectRelatedTask,
        onAssignToMe: taskActions.onAssignToMe,
        isAssigning: taskActions.assigningTaskId?.has?.(openTask.task.id),
        onUnassign: taskActions.onUnassignTask,
        isUnassigning: taskActions.unassigningTaskId === openTask.task.id,
        onMarkComplete: taskActions.onMarkComplete,
        isMarking: taskActions.markingTaskId === openTask.task.id,
        onCancelTask: taskActions.onCancelTask,
        isCanceling: taskActions.cancelingTaskId === openTask.task.id,
        onReassign: taskActions.onReassignTask,
        isReassigning: taskActions.reassigningTaskId === openTask.task.id,
        onFollowTask: taskActions.onFollowTask,
        isFollowing: taskActions.followingTaskId === openTask.task.id,
        onUnfollowTask: taskActions.onUnfollowTask,
        isUnfollowing: taskActions.unfollowingTaskId === openTask.task.id,
        onBlockTask: taskActions.onBlockTask,
        isBlocking: taskActions.blockingTaskId === openTask.task.id,
        onUnblockTask: taskActions.onUnblockTask,
        isUnblocking: taskActions.unblockingTaskId === openTask.task.id,
        onEscalateTask: taskActions.onEscalateTask,
        isEscalating: taskActions.escalatingTaskId === openTask.task.id,
        onDeescalateTask: taskActions.onDeescalateTask,
        isDeescalating: taskActions.deescalatingTaskId === openTask.task.id,
        onDelegateSubtasks: taskActions.onDelegateSubtasks,
        isDelegatingSubtasks: taskActions.delegatingSubtasksTaskId === openTask.task.id,
        onCancelSubtask: taskActions.onCancelSubtask,
        cancelingSubtaskId: taskActions.cancelingSubtaskId,
        currentUserId: taskActions.currentUserId,
        currentUserName: taskActions.currentUserName,
        cortexOrigin: taskActions.cortexOrigin,
        onReplySent: taskActions.onReplySent,
      }}
    />
  );

  if (page === ACTION_AREA_PAGE.PostVisit) {
    return (
      <div className={styles.root}>
        <PostVisitView
          postFilteredByDate={postFilteredByDate}
          postVisitAppointmentTasksById={postVisitAppointmentTasksById}
          scopeLabel={scopeLabel}
          todayKey={todayKey}
          taskActions={taskActions}
          onOpenTask={handleOpenTask}
          onBack={() => setPage(ACTION_AREA_PAGE.Huddle)}
        />
        {taskSheet}
      </div>
    );
  }

  if (page === ACTION_AREA_PAGE.VisitsDue) {
    return (
      <div className={styles.root}>
        <ActionAreaHeader active={ACTION_AREA_PAGE.VisitsDue} onChange={setPage} />
        <VisitsDueView
          organizationScope={organizationScope}
          departments={departments}
          scopeFilters={scopeFilters}
          onChangeScopeFilters={onChangeScopeFilters}
          guideEnabled={guideEnabled}
          onToggleGuide={onToggleGuide}
          isScopePaused={isScopePaused}
          onToggleScopePause={onToggleScopePause}
          isPinnedToCurrentScope={isPinnedToCurrentScope}
          canResetToPinnedScope={canResetToPinnedScope}
          showScopeUtilities={showScopeUtilities}
          onToggleScopePin={onToggleScopePin}
          onResetScopeToPinned={onResetScopeToPinned}
        />
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <ActionAreaHeader active={ACTION_AREA_PAGE.Huddle} onChange={setPage} />

      <div className={styles.toolbar}>
        <ScopeFilterGroup
          departments={departments}
          filters={scopeFilters}
          onChangeFilters={onChangeScopeFilters}
          guideEnabled={guideEnabled}
          onToggleGuide={onToggleGuide}
          showUtilities={showScopeUtilities}
          isPinned={isPinnedToCurrentScope}
          onTogglePin={onToggleScopePin}
          canReset={canResetToPinnedScope}
          onReset={onResetScopeToPinned}
          isPaused={isScopePaused}
          onTogglePause={onToggleScopePause}
        />
      </div>

      <div className={styles.periodRow}>
        <div className={styles.presetGroup} role="group" aria-label="Period">
          {presets.map((preset) => {
            const active = isSamePeriod(period, preset.value);
            return (
              <button
                key={preset.label}
                type="button"
                aria-pressed={active}
                className={`${styles.presetButton} ${active ? styles.presetButtonActive : ''}`}
                onClick={() => setPeriod(preset.value)}
              >
                {preset.label}
              </button>
            );
          })}
        </div>

        <div className={styles.dayChips} role="group" aria-label="Clinic days">
          {chipDays.map((dayKey) => {
            const selected = period.includes(dayKey);
            const count = countForDay(dayKey);
            const label =
              dayKey === todayKey ? 'Today' : `${formatWeekday(dayKey)} ${Number(dayKey.slice(8))}`;
            return (
              <DayChip
                key={dayKey}
                label={label}
                count={count}
                selected={selected}
                onClick={() => setPeriod((prev) => togglePeriodDay(prev, dayKey))}
              />
            );
          })}
        </div>

        <label className={styles.pickDateLabel} htmlFor="action-area-pick-date">
          Pick a date
          <input
            id="action-area-pick-date"
            type="date"
            value={pickedDate}
            className={styles.dateInput}
            onChange={(event) => {
              if (event.target.value) setPeriod([event.target.value]);
            }}
          />
        </label>

        <span className={styles.readout}>
          {isLoading ? (
            <CircleLoader color={ColorsNew.darkGreen} strokeWidth={2} circleRadius={8} />
          ) : (
            <>
              {periodLabel} · {periodPatientCount} patient{periodPatientCount === 1 ? '' : 's'} ·{' '}
              {scopeLabel}
            </>
          )}
        </span>
      </div>

      <div className={styles.handoffCard}>
        <History size={18} className={styles.handoffIcon} aria-hidden="true" />
        <div className={styles.handoffText}>
          <h3 className={styles.handoffTitle}>Post-visit follow-up</h3>
          <p className={styles.handoffSubtitle}>
            {postVisitIsLoading
              ? 'Loading…'
              : postVisitSummary.open === 0
                ? `Nothing still open from the last ${POST_VISIT_WINDOW_DAYS} days.`
                : `${postVisitSummary.open} open across ${postVisitSummary.visits} ${
                    postVisitSummary.visits === 1 ? 'visit' : 'visits'
                  } in the last ${POST_VISIT_WINDOW_DAYS} days.`}
          </p>
        </div>
        <button
          type="button"
          className={styles.handoffButton}
          onClick={() => setPage(ACTION_AREA_PAGE.PostVisit)}
        >
          View post-visit tasks
          <ArrowRight size={14} aria-hidden="true" />
        </button>
      </div>

      <div className={styles.dayList}>
        <div className={styles.dayListHeader}>
          <h3 className={styles.dayListTitle}>{listTitle}</h3>
          <span className={styles.dayListCount}>{periodAppts.length}</span>
          <span className={styles.dayListSummary}>{periodSummaryLabel}</span>
        </div>

        {!isLoading && periodDayGroups.every((group) => group.appts.length === 0) && (
          <p className={styles.emptyState}>No visits on the selected days.</p>
        )}

        {periodDayGroups.map(
          (group) =>
            group.appts.length > 0 && (
              <div key={group.dayKey} className={styles.dayGroup}>
                <DayHeading heading={getDayHeadingLabel(todayKey, group.dayKey)} />
                <div className={styles.dayRows}>
                  {group.appts.map((appt) => (
                    <HuddleVisitCard
                      key={appt.id}
                      appt={appt}
                      tasks={appointmentTasksById[appt.id] || []}
                      markingTaskId={taskActions.markingTaskId}
                      assigningTaskId={taskActions.assigningTaskId}
                      onMarkComplete={taskActions.onMarkComplete}
                      onAssignToMe={taskActions.onAssignToMe}
                      onOpenTask={(task) =>
                        handleOpenTask(task, appt.id, appointmentTasksById[appt.id] || [])
                      }
                      notes={notesByApptId[appt.id] || []}
                      onPostNote={(text) => handlePostNote(appt, text)}
                    />
                  ))}
                </div>
              </div>
            ),
        )}
      </div>

      {taskSheet}
    </div>
  );
};

ActionAreaView.propTypes = {
  isLoading: PropTypes.bool,
  apptsByDate: PropTypes.object,
  postVisitIsLoading: PropTypes.bool,
  postApptsByDate: PropTypes.object,
  postVisitAppointmentTasksById: PropTypes.object,
  appointmentTasksById: PropTypes.object,
  organizationScope: PropTypes.string,
  taskActions: PropTypes.object.isRequired,
  departments: PropTypes.array,
  scopeFilters: PropTypes.shape({
    regions: PropTypes.arrayOf(PropTypes.string),
    counties: PropTypes.arrayOf(PropTypes.string),
    markets: PropTypes.arrayOf(PropTypes.string),
    departments: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  onChangeScopeFilters: PropTypes.func.isRequired,
  guideEnabled: PropTypes.bool,
  onToggleGuide: PropTypes.func,
  isScopePaused: PropTypes.bool,
  onToggleScopePause: PropTypes.func,
  isPinnedToCurrentScope: PropTypes.bool,
  canResetToPinnedScope: PropTypes.bool,
  showScopeUtilities: PropTypes.bool,
  onToggleScopePin: PropTypes.func,
  onResetScopeToPinned: PropTypes.func,
};

export default ActionAreaView;
